import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, api, ADMIN_TOKEN_KEY } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import AdminActivityTab from '@/pages/AdminActivityTab';
import {
  ShieldCheck,
  LogOut,
  Plus,
  FileText,
  ExternalLink,
  Trash2,
  Loader2,
  Search,
  BookOpen,
  Save,
  Activity,
} from 'lucide-react';

const ADMIN_TRACKS = ['JEE', 'NEET'];
const CLASSES = [11, 12];
const QBANK_SUBJECTS = ['Physics', 'Chemistry', 'Maths', 'Biology', 'English'];

export default function AdminDashboardPage() {
  const nav = useNavigate();
  const [syllabus, setSyllabus] = useState(null);
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [track, setTrack] = useState('JEE');
  const [klass, setKlass] = useState(11);
  const [subject, setSubject] = useState('');
  const [chapter, setChapter] = useState('');
  const [driveUrl, setDriveUrl] = useState('');
  const [saving, setSaving] = useState(false);

  // Filter state (table)
  const [filterTrack, setFilterTrack] = useState('');
  const [filterKlass, setFilterKlass] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [q, setQ] = useState('');

  // Q-Bank state
  const [qbankRows, setQbankRows] = useState([]); // array of {subject, drive_url, sample_url, id, updated_at}
  const [qbankSaving, setQbankSaving] = useState('');

  const refreshQbank = useCallback(async () => {
    try {
      const { data } = await adminApi.get('/admin/qbank');
      setQbankRows(data.items || []);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) return;
      toast.error('Failed to load Q-Bank entries');
    }
  }, []);

  const updateQbankField = (subject, field, value) => {
    setQbankRows((rows) =>
      rows.map((r) => (r.subject === subject ? { ...r, [field]: value } : r)),
    );
  };

  const saveQbank = async (subject) => {
    const row = qbankRows.find((r) => r.subject === subject);
    if (!row) return;
    setQbankSaving(subject);
    try {
      await adminApi.post('/admin/qbank', {
        subject,
        drive_url: (row.drive_url || '').trim(),
        sample_url: (row.sample_url || '').trim(),
      });
      toast.success(`${subject} Q-Bank saved`);
      await refreshQbank();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setQbankSaving('');
    }
  };

  const refreshList = useCallback(async () => {
    try {
      const params = {};
      if (filterTrack) params.track = filterTrack;
      if (filterKlass) params.klass = filterKlass;
      if (filterSubject) params.subject = filterSubject;
      const { data } = await adminApi.get('/admin/set3', { params });
      setItems(data.items || []);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        nav('/admin/login', { replace: true });
        return;
      }
      toast.error('Failed to load Set 3 entries');
    }
  }, [filterTrack, filterKlass, filterSubject, nav]);

  useEffect(() => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!token) {
      nav('/admin/login', { replace: true });
      return;
    }
    (async () => {
      try {
        const [{ data: meData }, { data: syl }] = await Promise.all([
          adminApi.get('/admin/me'),
          api.get('/syllabus'),
        ]);
        setMe(meData);
        setSyllabus(syl);
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          localStorage.removeItem(ADMIN_TOKEN_KEY);
          nav('/admin/login', { replace: true });
          return;
        }
        toast.error('Failed to load admin panel');
      }
      await refreshList();
      await refreshQbank();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading) refreshList();
  }, [filterTrack, filterKlass, filterSubject, loading, refreshList]);

  const subjectsForTrack = useMemo(() => {
    if (!syllabus) return [];
    return syllabus.track_subjects?.[track] || [];
  }, [syllabus, track]);

  const chaptersForSelection = useMemo(() => {
    if (!syllabus || !subject) return [];
    return syllabus.syllabus?.[klass]?.[subject] || [];
  }, [syllabus, klass, subject]);

  // Reset subject/chapter if they become invalid after track/klass change
  useEffect(() => {
    if (subject && !subjectsForTrack.includes(subject)) {
      setSubject('');
      setChapter('');
    }
  }, [subjectsForTrack, subject]);
  useEffect(() => {
    if (chapter && !chaptersForSelection.includes(chapter)) {
      setChapter('');
    }
  }, [chaptersForSelection, chapter]);

  const canSave = track && klass && subject && chapter && driveUrl.trim().startsWith('http');

  const onSave = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    try {
      const { data } = await adminApi.post('/admin/set3', {
        track,
        klass: Number(klass),
        subject,
        chapter,
        drive_url: driveUrl.trim(),
      });
      toast.success(`Set 3 ${data.action === 'updated' ? 'updated' : 'saved'} for ${chapter}`);
      setDriveUrl('');
      setChapter('');
      await refreshList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (item) => {
    try {
      await adminApi.delete(`/admin/set3/${item.id}`);
      toast.success('Set 3 entry deleted');
      await refreshList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const onLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    nav('/admin/login', { replace: true });
  };

  const filteredItems = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter(
      (it) =>
        it.chapter.toLowerCase().includes(term) ||
        it.subject.toLowerCase().includes(term) ||
        it.track.toLowerCase().includes(term) ||
        (it.drive_url || '').toLowerCase().includes(term),
    );
  }, [items, q]);

  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center bg-neutral-950 text-neutral-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      {/* Header */}
      <header className="border-b border-red-500/20 bg-black/60 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-red-500" />
            <div className="font-display text-lg font-bold">StudyTrack AI · Admin</div>
            <Badge variant="outline" className="ml-2 border-red-500/40 text-xs text-red-300">
              Set 3 · Q-Bank · Activity
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-neutral-400 sm:inline" data-testid="admin-email-display">
              {me?.email}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              className="gap-1.5 text-neutral-400 hover:text-white"
              data-testid="admin-logout"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-6 px-4 py-8">
        <Tabs defaultValue="set3" className="space-y-6">
          <TabsList className="grid w-full max-w-xl grid-cols-3 bg-neutral-900">
            <TabsTrigger value="set3" data-testid="admin-tab-set3">
              <FileText className="mr-1.5 h-4 w-4" />
              Set 3 · Chapterwise
            </TabsTrigger>
            <TabsTrigger value="qbank" data-testid="admin-tab-qbank">
              <BookOpen className="mr-1.5 h-4 w-4" />
              Boards Q-Bank
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid="admin-tab-activity">
              <Activity className="mr-1.5 h-4 w-4" />
              Activity
            </TabsTrigger>
          </TabsList>

          <TabsContent value="set3" className="space-y-6">
        {/* Add / update form */}
        <Card className="border-red-500/20 bg-neutral-950/70">
          <CardContent className="space-y-5 p-6 sm:p-8">
            <div className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-red-500" />
              <div className="font-display text-xl font-semibold">Add or update Set 3 link</div>
            </div>
            <form onSubmit={onSave} className="grid gap-4 md:grid-cols-2" data-testid="set3-form">
              <div className="space-y-1.5">
                <Label>Track</Label>
                <Select value={track} onValueChange={setTrack}>
                  <SelectTrigger data-testid="track-select" className="h-11">
                    <SelectValue placeholder="Track" />
                  </SelectTrigger>
                  <SelectContent>
                    {ADMIN_TRACKS.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Class</Label>
                <Select value={String(klass)} onValueChange={(v) => setKlass(Number(v))}>
                  <SelectTrigger data-testid="klass-select" className="h-11">
                    <SelectValue placeholder="Class" />
                  </SelectTrigger>
                  <SelectContent>
                    {CLASSES.map((c) => (
                      <SelectItem key={c} value={String(c)}>
                        Class {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Subject</Label>
                <Select value={subject} onValueChange={setSubject}>
                  <SelectTrigger data-testid="subject-select" className="h-11">
                    <SelectValue placeholder="Choose subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {subjectsForTrack.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Chapter</Label>
                <Select value={chapter} onValueChange={setChapter} disabled={!subject}>
                  <SelectTrigger data-testid="chapter-select" className="h-11">
                    <SelectValue placeholder={subject ? 'Choose chapter' : 'Select subject first'} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {chaptersForSelection.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5 md:col-span-2">
                <Label htmlFor="drive-url">Google Drive URL</Label>
                <Input
                  id="drive-url"
                  value={driveUrl}
                  onChange={(e) => setDriveUrl(e.target.value)}
                  placeholder="https://drive.google.com/file/d/..."
                  required
                  data-testid="drive-url-input"
                  className="h-11"
                />
                <div className="text-xs text-neutral-500">
                  Paste the public / anyone-with-link Google Drive share URL. Students will open this in a new tab.
                </div>
              </div>

              <div className="md:col-span-2">
                <Button
                  type="submit"
                  disabled={!canSave || saving}
                  className="h-11 w-full bg-red-600 font-semibold hover:bg-red-500 md:w-auto"
                  data-testid="save-set3-button"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <FileText className="mr-2 h-4 w-4" />
                      Save Set 3 for this chapter
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Existing entries */}
        <Card className="border-neutral-800 bg-neutral-950/70">
          <CardContent className="space-y-4 p-6 sm:p-8">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="font-display text-lg font-semibold">Existing Set 3 mappings</div>
              <Badge variant="secondary" data-testid="set3-total-count">
                {filteredItems.length} / {items.length}
              </Badge>
            </div>

            <div className="grid gap-3 md:grid-cols-[1fr_1fr_1fr_2fr]">
              <Select
                value={filterTrack || 'ALL'}
                onValueChange={(v) => setFilterTrack(v === 'ALL' ? '' : v)}
              >
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Filter track" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All tracks</SelectItem>
                  {ADMIN_TRACKS.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filterKlass || 'ALL'}
                onValueChange={(v) => setFilterKlass(v === 'ALL' ? '' : v)}
              >
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Filter class" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All classes</SelectItem>
                  {CLASSES.map((c) => (
                    <SelectItem key={c} value={String(c)}>
                      Class {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filterSubject || 'ALL'}
                onValueChange={(v) => setFilterSubject(v === 'ALL' ? '' : v)}
              >
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Filter subject" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All subjects</SelectItem>
                  {(syllabus?.track_subjects?.JEE || [])
                    .concat(syllabus?.track_subjects?.NEET || [])
                    .filter((s, i, arr) => arr.indexOf(s) === i)
                    .map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search chapter, URL…"
                  className="h-10 pl-9"
                  data-testid="set3-search"
                />
              </div>
            </div>

            {filteredItems.length === 0 ? (
              <div className="rounded-lg border border-dashed border-neutral-800 py-10 text-center text-sm text-neutral-500">
                No Set 3 entries yet. Add one above to make it live for students.
              </div>
            ) : (
              <ul className="space-y-2">
                {filteredItems.map((it) => (
                  <li
                    key={it.id}
                    className="grid grid-cols-1 gap-3 rounded-lg border border-neutral-800 bg-neutral-900/40 p-3 md:grid-cols-[minmax(0,1fr)_auto]"
                    data-testid={`set3-row-${it.id}`}
                  >
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className="bg-red-600 text-white">{it.track}</Badge>
                        <Badge variant="outline">Class {it.klass}</Badge>
                        <Badge variant="outline">{it.subject}</Badge>
                        <span className="font-medium">{it.chapter}</span>
                      </div>
                      <a
                        href={it.drive_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block truncate text-xs text-red-400 hover:underline"
                        title={it.drive_url}
                      >
                        {it.drive_url}
                      </a>
                      <div className="text-[11px] text-neutral-500">
                        Updated {new Date(it.updated_at).toLocaleString()} · by {it.updated_by || 'admin'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <a
                        href={it.drive_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-9 items-center gap-1.5 rounded-md border border-neutral-700 px-3 text-sm hover:bg-neutral-800"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Open
                      </a>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="destructive"
                            size="sm"
                            className="gap-1.5"
                            data-testid={`delete-set3-${it.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Set 3 entry?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Students will immediately stop seeing the Set 3 button on <strong>{it.chapter}</strong> ({it.track} · Class {it.klass}).
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => onDelete(it)}
                              className="bg-red-600 hover:bg-red-500"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="qbank" className="space-y-6">
            <Card className="border-red-500/20 bg-neutral-950/70">
              <CardContent className="space-y-5 p-6 sm:p-8">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-red-500" />
                    <div className="font-display text-xl font-semibold">Boards Q-Bank · Class 12</div>
                    <Badge className="bg-red-600 text-white">Highest Seller</Badge>
                  </div>
                  <div className="text-xs text-neutral-400">
                    One main Drive link + one sample Drive link per subject.
                  </div>
                </div>
                <div className="space-y-3">
                  {QBANK_SUBJECTS.map((subject) => {
                    const row = qbankRows.find((r) => r.subject === subject) || {
                      subject,
                      drive_url: '',
                      sample_url: '',
                    };
                    const saving = qbankSaving === subject;
                    return (
                      <div
                        key={subject}
                        className="rounded-xl border border-neutral-800 bg-neutral-900/40 p-4"
                        data-testid={`admin-qbank-row-${subject}`}
                      >
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <div className="font-display text-base font-semibold">{subject}</div>
                            {row.updated_at && (
                              <span className="text-[11px] text-neutral-500">
                                · updated {new Date(row.updated_at).toLocaleString()}
                              </span>
                            )}
                          </div>
                          <Button
                            size="sm"
                            onClick={() => saveQbank(subject)}
                            disabled={saving}
                            className="gap-1.5 bg-red-600 hover:bg-red-500"
                            data-testid={`admin-qbank-save-${subject}`}
                          >
                            {saving ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Save className="h-3.5 w-3.5" />
                            )}
                            Save
                          </Button>
                        </div>
                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="space-y-1.5">
                            <Label htmlFor={`qbank-full-${subject}`} className="text-xs">
                              Full Q-Bank Drive URL <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              id={`qbank-full-${subject}`}
                              value={row.drive_url || ''}
                              onChange={(e) => updateQbankField(subject, 'drive_url', e.target.value)}
                              placeholder="https://drive.google.com/file/d/..."
                              className="h-10"
                              data-testid={`admin-qbank-full-${subject}`}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label htmlFor={`qbank-sample-${subject}`} className="text-xs">
                              Sample Drive URL (free preview)
                            </Label>
                            <Input
                              id={`qbank-sample-${subject}`}
                              value={row.sample_url || ''}
                              onChange={(e) => updateQbankField(subject, 'sample_url', e.target.value)}
                              placeholder="https://drive.google.com/file/d/..."
                              className="h-10"
                              data-testid={`admin-qbank-sample-${subject}`}
                            />
                          </div>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {row.sample_url && (
                            <a
                              href={row.sample_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex h-8 items-center gap-1 rounded-md border border-neutral-700 px-2.5 text-xs hover:bg-neutral-800"
                            >
                              <ExternalLink className="h-3 w-3" />
                              Test sample
                            </a>
                          )}
                          {row.drive_url && (
                            <a
                              href={row.drive_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex h-8 items-center gap-1 rounded-md border border-neutral-700 px-2.5 text-xs hover:bg-neutral-800"
                            >
                              <ExternalLink className="h-3 w-3" />
                              Test full
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="rounded-lg border border-red-500/25 bg-red-500/5 p-3 text-xs text-red-300">
                  ⚠️ Only paid students (Boards Q-Bank ₹999 or All Access) see the full Drive URL.
                  Sample URLs are visible to any logged-in student.
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="activity" className="space-y-6">
            <AdminActivityTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
